import { Injectable } from '@angular/core';

export const FIRE_CONSTRAINTS = {
  currentAge:      { min: 18,    max: 70,          step: 1 },
  retirementAge:   { min: 25,    max: 75,          step: 1 },
  lifeExpectancy:  { min: 40,    max: 100,         step: 1 },
  monthlyExpenses: { min: 5_000, max: 2_000_000,   step: 1_000 },
  currentSavings:  { min: 0,     max: 200_000_000, step: 10_000 },
  monthlySip:      { min: 0,     max: 2_000_000,   step: 500 },
  preReturn:       { min: 1,     max: 30,          step: 0.1 },
  postReturn:      { min: 1,     max: 30,          step: 0.1 },
  inflation:       { min: 0,     max: 15,          step: 0.1 },
  swr:             { min: 2,     max: 10,          step: 0.1 }
} as const;

export interface FireInput {
  currentAge: number;
  retirementAge: number;
  lifeExpectancy: number;
  currentMonthlyExpense: number;
  currentSavings: number;
  monthlySip: number;
  preReturnRate: number;
  postReturnRate: number;
  inflationRate: number;
  swr: number;
}

export interface FireYearRow {
  year: number;
  age: number;
  phase: 'accumulating' | 'retired';
  annualExpense: number;
  requiredCorpus: number;
  investedCorpus: number;
}

export interface FireResult {
  years: FireYearRow[];
  fireAge: number | null;
  yearsToFire: number | null;
  corpusAtRetirement: number;
  requiredCorpusAtRetirement: number;
  surplus: number;
  totalInvested: number;
  totalWealthGain: number;
  depletionAge: number | null;
  corpusAtLifeExpectancy: number;
  requiredMonthlySip: number;
}

@Injectable({ providedIn: 'root' })
export class FireCalculatorService {

  calculate(input: FireInput): FireResult {
    const {
      currentAge, retirementAge, lifeExpectancy,
      currentMonthlyExpense, currentSavings, monthlySip,
      preReturnRate, postReturnRate, inflationRate, swr
    } = input;

    const preMonthlyRate  = preReturnRate / 100 / 12;
    const postMonthlyRate = postReturnRate / 100 / 12;
    const totalMonths     = (lifeExpectancy - currentAge) * 12;
    const retirementMonth = (retirementAge - currentAge) * 12;

    const requiredCorpusAt = (m: number): number => {
      const annualExpense = currentMonthlyExpense * 12 * Math.pow(1 + inflationRate / 100, m / 12);
      return annualExpense / (swr / 100);
    };

    // Pass 1: pure accumulation projection (SIP kept going) to find when invested corpus first
    // crosses the inflation-adjusted FI number — this is the "FIRE age", independent of the
    // retirement age the user actually plans around.
    let fiBalance = currentSavings;
    let fireMonth: number | null = null;
    for (let m = 1; m <= totalMonths; m++) {
      fiBalance = (fiBalance + monthlySip) * (1 + preMonthlyRate);
      if (fireMonth === null && fiBalance >= requiredCorpusAt(m)) fireMonth = m;
    }
    const fireAge     = fireMonth !== null ? currentAge + fireMonth / 12 : null;
    const yearsToFire = fireMonth !== null ? fireMonth / 12 : null;

    // Pass 2: life-cycle simulation around the chosen retirement age — accumulate with SIP until
    // retirement, then draw down inflation-adjusted expenses against the post-retirement return.
    let balance = currentSavings;
    let totalInvested = currentSavings;
    let depletedAtMonth: number | null = null;

    interface MonthRow { balance: number; requiredCorpus: number; annualExpense: number; phase: 'accumulating' | 'retired'; }
    const months: MonthRow[] = [];

    for (let m = 1; m <= totalMonths; m++) {
      const annualExpense  = currentMonthlyExpense * 12 * Math.pow(1 + inflationRate / 100, m / 12);
      const requiredCorpus = annualExpense / (swr / 100);
      const phase: 'accumulating' | 'retired' = m <= retirementMonth ? 'accumulating' : 'retired';

      if (phase === 'accumulating') {
        balance = (balance + monthlySip) * (1 + preMonthlyRate);
        totalInvested += monthlySip;
      } else {
        balance = balance * (1 + postMonthlyRate) - annualExpense / 12;
        if (balance <= 0) {
          balance = 0;
          if (depletedAtMonth === null) depletedAtMonth = m;
        }
      }
      months.push({ balance, requiredCorpus, annualExpense, phase });
    }

    const years: FireYearRow[] = [];
    for (let y = 1; y <= Math.ceil(totalMonths / 12); y++) {
      const row = months[Math.min(y * 12, totalMonths) - 1];
      years.push({
        year: y,
        age: currentAge + y,
        phase: row.phase,
        annualExpense: row.annualExpense,
        requiredCorpus: row.requiredCorpus,
        investedCorpus: row.balance
      });
    }

    const retirementIdx = Math.min(retirementMonth, months.length) - 1;
    const corpusAtRetirement         = retirementMonth > 0 ? months[retirementIdx].balance : currentSavings;
    const requiredCorpusAtRetirement = retirementMonth > 0 ? months[retirementIdx].requiredCorpus : requiredCorpusAt(0);
    const surplus = corpusAtRetirement - requiredCorpusAtRetirement;

    const corpusAtLifeExpectancy = months.length ? months[months.length - 1].balance : balance;
    const depletionAge = depletedAtMonth !== null ? currentAge + depletedAtMonth / 12 : null;

    // Monthly SIP that would exactly close a shortfall by the target retirement age.
    let requiredMonthlySip = monthlySip;
    if (retirementMonth > 0) {
      const n = retirementMonth;
      const i = preMonthlyRate;
      const fvLumpsum = currentSavings * Math.pow(1 + i, n);
      const neededFromSip = requiredCorpusAtRetirement - fvLumpsum;
      if (neededFromSip <= 0) {
        requiredMonthlySip = 0;
      } else {
        const annuityFactor = ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
        requiredMonthlySip = neededFromSip / annuityFactor;
      }
    }

    return {
      years, fireAge, yearsToFire,
      corpusAtRetirement, requiredCorpusAtRetirement, surplus,
      totalInvested, totalWealthGain: corpusAtRetirement - totalInvested,
      depletionAge, corpusAtLifeExpectancy, requiredMonthlySip
    };
  }
}
